import { forwardRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Input, type InputProps } from './Input.js'

export type PasswordInputProps = Omit<InputProps, 'type' | 'trailing'>

/**
 * A password field somebody can look at.
 *
 * Six fields on this desk were `type="password"` and nothing else: the one at
 * sign-in, the three on the change-password sheet, and the two on the reset
 * form. Every one of them was typed blind, and the two worst cases are the
 * ones where being wrong costs the most:
 *
 * - **Sign in.** A mistyped password is only found out after the request, and
 *   the screen then asks for a six-digit code from a phone. Getting the
 *   password wrong means going back for the phone again.
 * - **"And again".** A new password and its confirmation, both invisible. The
 *   second box exists *only* because the first one cannot be read — which is
 *   an odd way to solve a problem you could solve by letting somebody read it.
 *
 * The eye is a real button in the tab order, not an icon somebody has to find
 * with a mouse. `type="button"` matters: inside a form, a button without it
 * submits, so pressing "show" would have signed the person in.
 *
 * It does not hide itself again on blur. Somebody who reveals a password has
 * decided to; taking it back the moment they reach for something else means
 * the one action they wanted undone itself.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(props, ref) {
    const [shown, setShown] = useState(false)
    return (
      <Input
        ref={ref}
        type={shown ? 'text' : 'password'}
        trailing={
          <button
            type="button"
            /* Named for what pressing it does, not for what is on screen —
               "Show password" while it is hidden. A screen reader announcing
               "Hide password" over a hidden field says nothing useful. */
            aria-label={shown ? 'Hide password' : 'Show password'}
            aria-pressed={shown}
            disabled={props.disabled ?? false}
            onClick={() => setShown((s) => !s)}
            /* `-m-2 p-2` rather than `-m-1 p-1`: the icon is 16px and the
               target was 24, which is half a fingertip. The negative margin
               means the bigger target costs the field no width. */
            className="hover:text-ink focus-visible:ring-coral-fill/30 -m-2 flex rounded p-2 transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed"
          >
            {shown ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
          </button>
        }
        {...props}
      />
    )
  },
)
